/*
  Mouvement du thème Makalu.

  Le script est une amélioration progressive : il pose `motion-ready` sur
  `<html>`, ce qui active les états initiaux définis dans `motion.css`. Sans
  JavaScript, ou si le navigateur ne fournit pas `IntersectionObserver`, la
  classe n'est jamais posée et la page reste entièrement lisible.

  Il n'anime rien lui-même : il se contente de basculer des classes et d'écrire
  une propriété personnalisée. Toute la durée et la courbe vivent dans la
  feuille de style, ce qui garde un seul endroit à corriger.
*/

(() => {
  const root = document.documentElement;

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const supported = 'IntersectionObserver' in window && 'requestAnimationFrame' in window;

  if (!supported) return;

  root.classList.add('motion-ready');

  /* ---------- Apparition au défilement ---------- */

  /*
    Le décalage en cascade est calculé par groupe : les enfants d'un même
    conteneur `data-reveal-group` s'échelonnent, ce qui évite d'écrire un délai
    dans chaque gabarit.
  */
  const applyStagger = () => {
    document.querySelectorAll('[data-reveal-group]').forEach((group) => {
      const step = Number(group.dataset.revealGroup) || 90;
      group.querySelectorAll(':scope > [data-reveal]').forEach((child, index) => {
        child.style.setProperty('--reveal-delay', `${index * step}ms`);
      });
    });
  };

  const reveal = (element) => {
    element.classList.add('is-revealed');
    element.addEventListener(
      'transitionend',
      () => element.classList.add('is-settled'),
      { once: true }
    );
  };

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        reveal(entry.target);
        observer.unobserve(entry.target);
      });
    },
    // La marge basse déclenche l'apparition avant que l'élément touche le bord.
    { rootMargin: '0px 0px -12% 0px', threshold: 0.01 }
  );

  const watch = () => {
    applyStagger();
    document.querySelectorAll('[data-reveal]').forEach((element) => {
      // Ce qui est déjà à l'écran au chargement apparaît sans attendre un défilement.
      const box = element.getBoundingClientRect();
      if (box.top < window.innerHeight && box.bottom > 0) reveal(element);
      else observer.observe(element);
    });
  };

  /* ---------- En-tête escamotable ---------- */

  const header = document.querySelector('.header');

  const watchHeader = () => {
    if (!header) return;

    const threshold = header.offsetHeight;
    let previous = window.scrollY;
    let ticking = false;

    const update = () => {
      const current = window.scrollY;
      const goingDown = current > previous && current > threshold * 2;

      header.classList.toggle('header--hidden', goingDown);
      header.classList.toggle('header--stuck', current > threshold);

      previous = current;
      ticking = false;
    };

    window.addEventListener(
      'scroll',
      () => {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(update);
      },
      { passive: true }
    );
  };

  /* ---------- Parallaxe du héros ---------- */

  const watchHero = () => {
    const hero = document.querySelector('.hero');
    if (!hero || reducedMotion.matches) return;
    // Sous 900 px, la hauteur utile est trop courte : le décalage se verrait.
    if (window.matchMedia('(max-width: 899px)').matches) return;

    let ticking = false;

    const update = () => {
      const box = hero.getBoundingClientRect();
      if (box.bottom > 0 && box.top < window.innerHeight) {
        // 6 % de la hauteur au maximum : la photographie reste couvrante.
        const progress = Math.min(1, Math.max(0, -box.top / box.height));
        hero.style.setProperty('--hero-parallax', `${(progress * box.height * 0.06).toFixed(1)}px`);
      }
      ticking = false;
    };

    window.addEventListener(
      'scroll',
      () => {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(update);
      },
      { passive: true }
    );

    update();
  };

  /* ---------- Démarrage ---------- */

  const start = () => {
    watch();
    watchHeader();
    watchHero();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
